import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { urlDeFoto } from '../api/client';
import { useSession } from '../session/SessionContext';
import {
  AvisoDeDispositivos,
  CampanaDeDispositivos,
  usePendientesDeDispositivos,
} from './AvisoDeDispositivos';
import {
  IconoBitacora,
  IconoCategorias,
  IconoDispositivos,
  IconoDocumentos,
  IconoGrupos,
  IconoPanel,
  IconoSalir,
  IconoUsuarios,
} from './Iconos';

interface ItemNav {
  ruta: string;
  etiqueta: string;
  icono: ReactNode;
  permiso?: string;
  /** Módulo que todavía no existe: se muestra apagado y dice cuál lo trae. */
  pendiente?: string;
  /** Cuántas cosas esperan ahí a alguien; sin número, no se muestra nada. */
  contador?: number;
}

/**
 * El armazón envuelve las rutas.
 *
 * El título se toma de la ruta activa, no se pasa por props: así una ruta nueva
 * no puede quedarse sin título por olvido.
 */
export function Armazon() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { estado, puede, salir } = useSession();
  const sesion = estado.status === 'autenticado' ? estado.session : null;
  // Los dispositivos que esperan aprobación (ADR-0026): el contador del menú, la
  // campana y el aviso salen de la misma consulta.
  const apruebaDispositivos = puede('device.approve');
  const pendientes = usePendientesDeDispositivos(apruebaDispositivos);
  const dispositivosPendientes = pendientes?.pending ?? 0;

  const principales: ItemNav[] = [
    { ruta: '/panel', etiqueta: t('nav.dashboard'), icono: <IconoPanel /> },
    { ruta: '/documentos', etiqueta: t('nav.documents'), icono: <IconoDocumentos /> },
    { ruta: '/catalogo', etiqueta: t('nav.categories'), icono: <IconoCategorias /> },
    // Gerencia no administra el portal: da seguimiento a su gente. Va con lo que
    // se consulta, no con la administración.
    { ruta: '/equipo', etiqueta: t('nav.team'), icono: <IconoGrupos />, permiso: 'reading.team_report' },
  ];

  const administracion: ItemNav[] = [
    { ruta: '/consultas', etiqueta: t('nav.access'), icono: <IconoBitacora />, permiso: 'audit.read' },
    { ruta: '/lecturas', etiqueta: t('nav.readings'), icono: <IconoDocumentos />, permiso: 'audit.read' },
    { ruta: '/grupos', etiqueta: t('nav.groups'), icono: <IconoGrupos /> },
    { ruta: '/usuarios', etiqueta: t('nav.users'), icono: <IconoUsuarios />, permiso: 'user.invite' },
    {
      ruta: '/dispositivos',
      etiqueta: t('nav.devices'),
      icono: <IconoDispositivos />,
      permiso: 'device.approve',
      contador: dispositivosPendientes,
    },
    { ruta: '/correos', etiqueta: t('nav.emails'), icono: <IconoUsuarios />, permiso: 'user.manage' },
    { ruta: '/bitacora', etiqueta: t('nav.audit'), icono: <IconoBitacora />, permiso: 'audit.read' },
    { ruta: '/carga-inicial', etiqueta: t('nav.initialLoad'), icono: <IconoDocumentos />, permiso: 'system.initial_load' },
  ];

  const titulo =
    [...principales, ...administracion].find((item) => pathname.startsWith(item.ruta))?.etiqueta ??
    t('nav.dashboard');


  const iniciales = (sesion?.user.username ?? '??').slice(0, 2).toUpperCase();

  const renderItem = (item: ItemNav) => {
    // Lo que no se puede hacer, no se muestra.
    if (item.permiso && !puede(item.permiso)) return null;
    // Un módulo que aún no existe no es un enlace roto: se muestra apagado.
    if (item.pendiente) {
      return (
        <button key={item.ruta} type="button" className="nav__item" disabled>
          {item.icono}
          {item.etiqueta}
          <span className="nav__pendiente">{item.pendiente}</span>
        </button>
      );
    }

    return (
      <NavLink key={item.ruta} to={item.ruta} className="nav__item">
        {item.icono}
        {item.etiqueta}
        {item.contador ? (
          <span className="nav__contador" aria-label={t('nav.waiting', { count: item.contador })}>
            {item.contador}
          </span>
        ) : null}
      </NavLink>
    );
  };

  return (
    <div className="armazon">
      <aside className="lateral">
        <div className="marca">
          {/* El mismo escudo que la portada: la caja quiere reconocer adentro el
              sello con el que entró. Más grande que el monograma que había aquí
              —a 34 px el escudo entero era una mancha verde—; su anillo no se
              lee a este tamaño, pero la silueta sí, y es la misma de la portada. */}
          <img className="marca__escudo" src="/marca/escudo.png" alt="" aria-hidden="true" />
          <span className="marca__textos">
            <span className="marca__institucion">{t('app.institution')}</span>
            <span className="marca__nombre">{t('app.name')}</span>
          </span>
        </div>

        <nav className="nav" aria-label={t('nav.label')}>
          <span className="nav__grupo">{t('nav.consult')}</span>
          {principales.map(renderItem)}
          <span className="nav__grupo">{t('nav.administration')}</span>
          {administracion.map(renderItem)}
        </nav>

        <div className="lateral__pie">{t('app.moduleNotice')}</div>
      </aside>

      <div>
        <header className="superior">
          <span className="superior__titulo">{titulo}</span>
          <div className="superior__derecha">
            {apruebaDispositivos && <CampanaDeDispositivos pendientes={dispositivosPendientes} />}
            {/* El chip lleva al perfil: es donde la gente lo busca, y evita
                sumar un renglón más a una navegación que ya tiene ocho. */}
            <NavLink className="usuario-chip" to="/perfil">
              {sesion?.photoUpdatedAt ? (
                <img
                  className="avatar avatar--foto"
                  src={urlDeFoto(sesion.user.id, sesion.photoUpdatedAt) ?? undefined}
                  alt=""
                />
              ) : (
                <span className="avatar" aria-hidden="true">
                  {iniciales}
                </span>
              )}
              <span>
                <span className="usuario-chip__nombre">{sesion?.user.username}</span>
              </span>
            </NavLink>
            <button className="boton boton--fantasma" type="button" onClick={() => void salir()}>
              <IconoSalir /> {t('app.signOut')}
            </button>
          </div>
        </header>

        <main className="contenido">
          <Outlet />
        </main>
        {apruebaDispositivos && sesion && <AvisoDeDispositivos resumen={pendientes} userId={sesion.user.id} />}
      </div>
    </div>
  );
}
